import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Trash2, Search, Bell, BellOff, Loader2 } from 'lucide-react';
import { BrutalButton } from '@/components/ui/BrutalButton';
import { BrutalCard } from '@/components/ui/BrutalCard';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';

interface SavedSearch {
  id: string;
  search_name: string;
  search_params: any;
  notification_enabled: boolean;
  created_at: string;
  last_used: string;
}

const SavedSearches = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searches, setSearches] = useState<SavedSearch[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSavedSearches();
  }, []);

  const loadSavedSearches = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/auth/login');
        return;
      }

      const { data, error } = await supabase
        .from('saved_searches')
        .select('*')
        .eq('user_id', user.id)
        .order('last_used', { ascending: false });

      if (error) throw error;
      setSearches(data || []);
    } catch (error: any) {
      console.error('Error loading saved searches:', error);
      toast({
        title: t('common.error'),
        description: error.message,
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const executeSearch = async (search: SavedSearch) => {
    try {
      // Update last_used timestamp
      await supabase
        .from('saved_searches')
        .update({ last_used: new Date().toISOString() })
        .eq('id', search.id);

      // Navigate to professionals page with search params
      const params = new URLSearchParams();
      Object.entries(search.search_params).forEach(([key, value]) => {
        if (value) params.append(key, String(value));
      });
      navigate(`/professionals?${params.toString()}`);
    } catch (error: any) {
      console.error('Error executing search:', error);
      toast({
        title: t('common.error'),
        description: error.message,
        variant: 'error',
      });
    }
  };

  const toggleNotifications = async (searchId: string, currentState: boolean) => {
    try {
      const { error } = await supabase
        .from('saved_searches')
        .update({ notification_enabled: !currentState })
        .eq('id', searchId);

      if (error) throw error;

      setSearches(prev =>
        prev.map(s =>
          s.id === searchId ? { ...s, notification_enabled: !currentState } : s
        )
      );

      toast({
        title: t('common.success'),
        description: t('searches.notificationsUpdated'),
      });
    } catch (error: any) {
      console.error('Error toggling notifications:', error);
      toast({
        title: t('common.error'),
        description: error.message,
        variant: 'error',
      });
    }
  };

  const deleteSearch = async (searchId: string) => {
    if (!confirm(t('searches.confirmDelete'))) return;

    try {
      const { error } = await supabase
        .from('saved_searches')
        .delete()
        .eq('id', searchId);

      if (error) throw error;

      setSearches(prev => prev.filter(s => s.id !== searchId));
      toast({
        title: t('common.success'),
        description: t('searches.deleted'),
      });
    } catch (error: any) {
      console.error('Error deleting search:', error);
      toast({
        title: t('common.error'),
        description: error.message,
        variant: 'error',
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold mb-2">
            {t('searches.title')}
          </h1>
          <p className="text-muted-foreground">
            {t('searches.description')}
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : searches.length === 0 ? (
          <BrutalCard className="text-center py-12">
            <p className="text-muted-foreground mb-4">
              {t('searches.noSavedSearches')}
            </p>
            <BrutalButton onClick={() => navigate('/professionals')}>
              {t('searches.startSearching')}
            </BrutalButton>
          </BrutalCard>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {searches.map((search) => (
              <BrutalCard key={search.id} className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="font-heading font-bold text-lg">
                    {search.search_name}
                  </h3>
                  <button
                    onClick={() => toggleNotifications(search.id, search.notification_enabled)}
                    className="text-muted-foreground hover:text-primary transition-colors"
                  >
                    {search.notification_enabled ? (
                      <Bell className="w-5 h-5" />
                    ) : (
                      <BellOff className="w-5 h-5" />
                    )}
                  </button>
                </div>

                <div className="text-sm text-muted-foreground mb-4 space-y-1">
                  {search.search_params.q && (
                    <p>Query: {search.search_params.q}</p>
                  )}
                  {search.search_params.category && (
                    <p>Category: {search.search_params.category}</p>
                  )}
                  {search.search_params.city && (
                    <p>City: {search.search_params.city}</p>
                  )}
                </div>

                <div className="flex gap-2">
                  <BrutalButton
                    onClick={() => executeSearch(search)}
                    className="flex-1"
                    size="sm"
                  >
                    <Search className="w-4 h-4 mr-2" />
                    {t('searches.search')}
                  </BrutalButton>
                  <BrutalButton
                    onClick={() => deleteSearch(search.id)}
                    variant="outline"
                    size="sm"
                  >
                    <Trash2 className="w-4 h-4" />
                  </BrutalButton>
                </div>

                <p className="text-xs text-muted-foreground mt-3">
                  {t('searches.lastUsed')}: {new Date(search.last_used).toLocaleDateString()}
                </p>
              </BrutalCard>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default SavedSearches;
